import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, UserPlus, Trash2, Search, ShieldCheck, Shield, Crown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import api from '../../api/client';

const ROLE_META = {
  admin: {
    label: 'Admin',
    badge: 'bg-amber-500/15 gold-text',
    desc: 'Accesso completo alla dashboard, incluse impostazioni e chiavi di integrazione.',
  },
  staff: {
    label: 'Staff',
    badge: 'bg-sky-500/15 text-sky-300',
    desc: 'Accede alla dashboard admin ma non può modificare impostazioni sensibili e chiavi.',
  },
};

/**
 * Admin — Team tab. Members with dashboard access (admin/staff):
 * name, email, role/permissions, add via user search, remove (role → user).
 * Mutations are Super Admin only (backend enforced too).
 */
const AdminTeamPanel = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [amSuper, setAmSuper] = useState(false);
  const [meId, setMeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newRole, setNewRole] = useState('staff');
  const [addingId, setAddingId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/team');
      setItems(data.items || []);
      setAmSuper(!!data.am_super_admin);
      setMeId(data.me_id || '');
    } catch (e) {
      toast({
        title: 'Errore caricamento team',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/admin/users/search', { params: { q, limit: 8 } });
        const teamIds = new Set(items.map((m) => m.id));
        setResults((data || []).filter((u) => !teamIds.has(u.id)));
      } catch { setResults([]); } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, items]);

  const setRole = async (userId, role) => {
    try {
      await api.post(`/admin/users/${userId}/role`, { role });
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: 'Operazione non riuscita',
        description: typeof detail === 'string' ? detail.split('|').pop() : 'Riprova.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const addMember = async (user) => {
    setAddingId(user.id);
    const ok = await setRole(user.id, newRole);
    if (ok) {
      toast({ title: 'Membro aggiunto', description: `@${user.username} ora ha il ruolo ${ROLE_META[newRole].label}.` });
      setQuery('');
      setResults([]);
      await load();
    }
    setAddingId(null);
  };

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    const ok = await setRole(member.id, role);
    if (ok) {
      toast({ title: 'Ruolo aggiornato', description: `@${member.username} → ${ROLE_META[role].label}.` });
      await load();
    }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    const ok = await setRole(removeTarget.id, 'user');
    setRemoving(false);
    if (ok) {
      toast({ title: 'Membro rimosso', description: `@${removeTarget.username} non ha più accesso alla dashboard.` });
      setRemoveTarget(null);
      await load();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-4" data-testid="admin-team-panel">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <ShieldCheck className="h-4 w-4 gold-text" />
          Team
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Membri con accesso alla dashboard admin. <strong>Admin</strong>: accesso completo ·{' '}
          <strong>Staff</strong>: dashboard senza impostazioni sensibili e chiavi.
          {!amSuper && ' Sei in modalità sola lettura (solo il Super Admin può modificare i ruoli).'}
        </p>
      </div>

      {amSuper && (
        <div className="mb-5 rounded-lg border border-border bg-secondary/20 p-3" data-testid="team-add-box">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
            <UserPlus className="h-4 w-4" /> Aggiungi membro
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca per username, email o nome…"
                className="h-9 pl-8 text-sm"
                data-testid="team-search-input"
              />
            </div>
            <div className="flex items-center gap-1.5" data-testid="team-role-picker">
              {['staff', 'admin'].map((r) => (
                <button
                  key={r}
                  onClick={() => setNewRole(r)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    newRole === r
                      ? 'border-amber-500/60 bg-amber-500/15 gold-text'
                      : 'border-border text-muted-foreground hover:bg-white/5'
                  }`}
                  data-testid={`team-role-pick-${r}`}
                >
                  {ROLE_META[r].label}
                </button>
              ))}
            </div>
          </div>
          {searching && <p className="mt-2 text-xs text-muted-foreground">Ricerca…</p>}
          {results.length > 0 && (
            <div className="mt-2 divide-y divide-border overflow-hidden rounded-lg border border-border" data-testid="team-search-results">
              {results.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 bg-background px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">@{u.username}</p>
                    <p className="truncate text-xs text-muted-foreground">{u.email}{u.full_name ? ` · ${u.full_name}` : ''}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addMember(u)}
                    disabled={addingId === u.id}
                    className="gold-gradient shrink-0 text-black"
                    data-testid={`team-add-${u.id}`}
                  >
                    {addingId === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
                    <span className="ml-1">Aggiungi come {ROLE_META[newRole].label}</span>
                  </Button>
                </div>
              ))}
            </div>
          )}
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="team-search-empty">
              Nessun utente trovato (o già nel team). L&apos;utente deve prima registrarsi su Nymia.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="h-5 w-5 animate-spin gold-text" /></div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Membro</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Ruolo</th>
                <th className="hidden px-3 py-2 text-left md:table-cell">Permessi</th>
                {amSuper && <th className="w-32 px-3 py-2 text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((m) => {
                const meta = ROLE_META[m.role] || ROLE_META.staff;
                const isMe = m.id === meId;
                return (
                  <tr key={m.id} data-testid={`team-row-${m.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {m.avatar_url ? (
                          <img src={m.avatar_url} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover object-center" />
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-[11px] font-bold uppercase">
                            {(m.username || '?')[0]}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-semibold">
                            @{m.username}
                            {isMe && <span className="ml-1.5 rounded bg-white/10 px-1 text-[10px] font-normal text-muted-foreground">tu</span>}
                          </p>
                          {m.full_name && <p className="truncate text-xs text-muted-foreground">{m.full_name}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="max-w-[220px] truncate px-3 py-2.5 text-xs text-muted-foreground">{m.email}</td>
                    <td className="px-3 py-2.5">
                      {amSuper && !isMe ? (
                        <select
                          value={m.role}
                          onChange={(e) => changeRole(m, e.target.value)}
                          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                          data-testid={`team-role-select-${m.id}`}
                        >
                          <option value="staff">Staff</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${meta.badge}`}>
                          {m.role === 'admin' ? <Crown className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                          {meta.label}
                        </span>
                      )}
                    </td>
                    <td className="hidden px-3 py-2.5 text-xs text-muted-foreground md:table-cell">{meta.desc}</td>
                    {amSuper && (
                      <td className="px-3 py-2.5 text-right">
                        {!isMe && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRemoveTarget(m)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`team-remove-${m.id}`}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" /> Rimuovi
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="team-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere @{removeTarget?.username} dal team?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;account tornerà un utente normale e perderà l&apos;accesso alla dashboard admin.
              Il profilo e i contenuti non vengono toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing} data-testid="team-remove-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); removeMember(); }}
              disabled={removing}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="team-remove-confirm-btn"
            >
              {removing ? 'Rimozione…' : 'Rimuovi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default AdminTeamPanel;
